<template>
  <div class="sftp-inbound-settings">
    <!-- connectivity information -->
    <ak-field v-if="connectivityURL" label="SFTP Server Address">
      <ak-pack>
        <ak-pack-item>
          <div class="text-large color-primary">
            <strong>{{ connectivityURL }}</strong>
          </div>
        </ak-pack-item>
        <ak-pack-item>
          <ak-tooltip placement="right">
            <template #trigger>
              <ak-button
                color="grey-lighter"
                size="small"
                circle
                @click.stop.prevent="copyToClipboard(connectivityURL)"
              >
                <ak-icon type="clipboard" />
              </ak-button>
            </template>
            Copy to Clipboard
          </ak-tooltip>
        </ak-pack-item>
      </ak-pack>
    </ak-field>

    <!-- credentials -->
    <ak-pack class="sftp-inbound-settings__credentials">
      <!-- username -->
      <ak-pack-item class="sftp-inbound-settings__username">
        <ak-pack>
          <ak-pack-item :grow="true">
            <ak-field label="Username">
              <ak-input
                :model-value="username"
                :readonly="true"
                class="sftp-inbound-settings__credential-input"
              />
            </ak-field>
          </ak-pack-item>
          <ak-pack-item>
            <ak-tooltip placement="right">
              <template #trigger>
                <ak-button
                  color="grey-lighter"
                  size="small"
                  circle
                  @click.stop.prevent="copyToClipboard(username)"
                >
                  <ak-icon type="clipboard" />
                </ak-button>
              </template>
              Copy to Clipboard
            </ak-tooltip>
          </ak-pack-item>
        </ak-pack>
      </ak-pack-item>

      <!-- password -->
      <ak-pack-item class="sftp-inbound-settings__password">
        <ak-pack>
          <ak-pack-item :grow="true">
            <ak-field label="Password">
              <ak-input
                :model-value="password"
                :readonly="true"
                class="sftp-inbound-settings__credential-input"
                placeholder="********************"
              />
            </ak-field>
          </ak-pack-item>
          <ak-pack-item>
            <ak-tooltip placement="right">
              <template #trigger>
                <ak-button
                  v-if="password"
                  class="mr-tiny"
                  color="grey-lighter"
                  size="small"
                  circle
                  @click.stop.prevent="copyToClipboard(password)"
                >
                  <ak-icon type="clipboard" />
                </ak-button>
              </template>
              Copy to Clipboard
            </ak-tooltip>
            <ak-dropdown aria-role="list">
              <template #trigger>
                <ak-button circle color="white">
                  <ak-icon type="more-vertical" />
                </ak-button>
              </template>
              <template #default>
                <ak-dropdown-item
                  aria-role="listitem"
                  @click="refreshCredentials()"
                >
                  Refresh
                </ak-dropdown-item>
                <ak-dropdown-item
                  aria-role="listitem"
                  class="color-danger"
                  @click="revokeCredentials()"
                >
                  Revoke
                </ak-dropdown-item>
              </template>
            </ak-dropdown>
          </ak-pack-item>
        </ak-pack>
      </ak-pack-item>
    </ak-pack>

    <!-- password visibility warning -->
    <ak-notification
      v-if="password"
      :closable="false"
      class="mb sftp-inbound-settings__credentials-warning"
      type="warning"
      has-icon
    >
      Copy your password. It will not be accessible after page refresh.
    </ak-notification>

    <!-- allowed networks -->
    <ak-field
      label="Allowed Networks"
      class="sftp-inbound-settings__allowed-networks"
    >
      <template #helper>
        Specify the IP addresses of the services that will connect to the server
      </template>
      <div class="sftp-allowed-networks">
        <!-- allowed network input -->
        <div class="sftp-allowed-networks__input">
          <ak-pack spacing="none">
            <ak-pack-item :grow="true">
              <ak-field addons>
                <ak-input
                  v-model="allowedNetwork"
                  :disabled="!validAllowedNetworkCount"
                  maxlength="15"
                  aria-label="Allowed network"
                />
                <ak-button
                  :disabled="
                    !validAllowedNetwork ||
                    !validAllowedNetworkCount ||
                    service.working.value
                  "
                  @click="addAllowedNetwork"
                  >Add</ak-button
                >
              </ak-field>
            </ak-pack-item>
          </ak-pack>
        </div>
        <!-- allowed network list entries -->
        <div class="sftp-allowed-networks__list mt-tiny">
          <ak-pack
            class="sftp-allowed-networks__list-entries"
            :wrap="true"
            aria-role="list"
            spacing="none"
          >
            <ak-pack-item v-for="network in allowedNetworks" :key="network">
              <ak-tag>
                <ak-pack>
                  <ak-pack-item>{{ network }}</ak-pack-item>
                  <ak-pack-item>
                    <ak-button
                      class="m-0"
                      color="transparent"
                      size="tiny"
                      circle
                    >
                      <ak-icon
                        type="close"
                        @click="removeAllowedNetwork(network)"
                      />
                    </ak-button>
                  </ak-pack-item>
                </ak-pack>
              </ak-tag>
            </ak-pack-item>
          </ak-pack>
        </div>
      </div>
    </ak-field>

    <!-- Port -->
    <ak-field label="Port" class="width-150">
      <ak-input :disabled="true" aria-label="Port" :model-value="22" />
    </ak-field>
  </div>
</template>

<script>
import { computed, defineComponent, ref } from 'vue'
import { writeDataToClipboard } from '@akerolabs/akero-vue/src/utils/helpers.js'
import { DialogProgrammatic } from '@akerolabs/akero-vue/src/components/dialog'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'

import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Dropdown from '@akerolabs/akero-vue/src/components/dropdown/Dropdown.vue'
import DropdownItem from '@akerolabs/akero-vue/src/components/dropdown/DropdownItem.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Notification from '@akerolabs/akero-vue/src/components/notification/Notification.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Tag from '@akerolabs/akero-vue/src/components/tag/Tag.vue'

export default defineComponent({
  name: 'AkIntegrationSFTPInboundSettings',
  components: {
    [Button.name]: Button,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [Field.name]: Field,
    [Icon.name]: Icon,
    [Input.name]: Input,
    [Notification.name]: Notification,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Tag.name]: Tag,
  },
  provide() {
    return {
      akForm: undefined, // stub unneeded dependency
    }
  },
  props: {
    service: {
      required: true,
      type: Object,
    },
  },
  setup(props) {
    const validIPRegex = new RegExp('^(?:[0-9]{1,3}\\.){3}[0-9]{1,3}$')
    const maxAllowedNetworks = 20
    const allowedNetwork = ref('')

    /**
     * List of allowed networks
     * @type {WritableComputedRef<*>}
     */
    const allowedNetworks = computed({
      get: () => props.service.inboundSettings.allowedIPs,
      set: (value) => props.service.updateInboundSettings(value),
    })

    /**
     * Determine if the number of allowed networks is within the limit
     * @type {ComputedRef<boolean>}
     */
    const validAllowedNetworkCount = computed(
      () =>
        (props.service.inboundSettings.allowedIPs || []).length <
        maxAllowedNetworks,
    )

    /**
     * Determine if an allowed network is in the correct format
     * @type {ComputedRef<boolean>}
     */
    const validAllowedNetwork = computed(() =>
      validIPRegex.test(allowedNetwork.value),
    )

    /**
     * Add an allowed network
     */
    const addAllowedNetwork = () => {
      if (validAllowedNetwork.value) {
        const allowedNetworksUpdated = [
          ...new Set(
            (props.service.inboundSettings.allowedIPs || []).concat(
              allowedNetwork.value,
            ),
          ),
        ]

        const updatedSettings = { allowedIPs: allowedNetworksUpdated.slice(0) }
        allowedNetwork.value = ''
        updateInboundSettings(updatedSettings)
      }
    }

    /**
     * Remove an allowed network
     * @param {String} network
     */
    const removeAllowedNetwork = (network) => {
      const allowedNetworksUpdated = (
        props.service.inboundSettings.allowedIPs || []
      ).filter((entry) => entry !== network)
      const updatedSettings = { allowedIPs: allowedNetworksUpdated.slice(0) }
      updateInboundSettings(updatedSettings)
    }

    /**
     * Update inbound settings
     * @param {Object} settings
     */
    const updateInboundSettings = (settings) => {
      let succeeded = true
      if (props.service.working.value) return
      props.service
        .updateInboundSettings(settings)
        .then(() => (succeeded = true))
        .catch(() => (succeeded = false))
        .finally(() => {
          ToastProgrammatic.open({
            message: succeeded ? 'Updated settings' : 'Error updating settings',
            type: succeeded ? 'success' : 'danger',
          })
        })
    }

    /**
     * Copy server link to clipboard
     * @param value
     * @returns {Promise<void>}
     */
    const copyToClipboard = (value) =>
      writeDataToClipboard(value).then(() =>
        ToastProgrammatic.open({
          message: 'Copied to clipboard.',
          type: 'success',
        }),
      )

    /**
     * Return connectivity information
     * @type {ComputedRef<unknown>}
     */
    const connectivityURL = computed(
      () =>
        props.service.inboundSettings.connectivity &&
        props.service.inboundSettings.connectivity.url,
    )

    /**
     * Return username
     * @type {ComputedRef<*>}
     */
    const username = computed(() => props.service.inboundSettings.username)

    /**
     * Return uncovered password
     * @type {ComputedRef<*>}
     */
    const password = computed(() => props.service.inboundSettings.password)

    /**
     * Refresh credentials
     */
    const refreshCredentials = () =>
      DialogProgrammatic.confirm({
        title: `Refresh Credentials`,
        message: `
          <p>
            Are you sure you want to refresh credentials?
          </p>
        `,
        onConfirm: () => props.service.handleInboundCredentialsRefresh(),
      })

    /**
     * Revoke credentials
     */
    const revokeCredentials = () =>
      DialogProgrammatic.prompt({
        title: `Revoke Credentials`,
        message: `
          <p>
            Are you sure you want to revoke credentials?
          </p>
          <small><strong>Please type REVOKE to confirm</strong></small>
        `,
        inputAttrs: {
          pattern: 'REVOKE',
        },
        trapFocus: true,
        type: 'danger',
        hasIcon: true,
        size: 'small',
        confirmText: 'Yes',
        cancelText: 'No',
        onConfirm: () => props.service.handleInboundCredentialsRevoke(),
      })

    return {
      allowedNetwork,
      allowedNetworks,
      addAllowedNetwork,
      removeAllowedNetwork,
      validAllowedNetwork,
      validAllowedNetworkCount,
      copyToClipboard,
      connectivityURL,
      username,
      password,
      refreshCredentials,
      revokeCredentials,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@akerolabs/akero-vue/src/scss/_settings.scss';

.sftp-inbound-settings {
  &__credentials,
  &__credentials-warning {
    max-width: 900px;
  }

  &__credential-input {
    & > :deep(.is-disabled) {
      opacity: 1 !important;
    }
  }

  &__username {
    width: 35%;
  }

  &__password {
    width: 65%;
    margin-left: $spacing;
  }

  &__allowed-networks {
    max-width: 900px;
  }
}

.sftp-allowed-networks {
  :deep(.ak-form-group-wrap) {
    margin-bottom: 0 !important;
  }

  &__input {
    max-width: 50%;

    // flush controls
    :deep(.ak-form-control) {
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }

    :deep(.ak-btn) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      margin-left: -1px;
    }
  }

  &__list {
    margin-top: $spacing-small;
  }

  &__list-entries {
    gap: $spacing-small;
  }
}
</style>
